import { Box } from "@mui/material";
import Link from "next/link";
import { useId, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_EASE,
  SITE_MEDIA,
  SITE_TYPE,
  SITE_VAR,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { ClosingContact } from "../siteKit/ClosingContact";
import { LinkCard } from "../siteKit/LinkCard";
import { SiteFileImage } from "../siteKit/media/SiteFileImage";
import { SiteImage } from "../siteKit/media/SiteImage";
import { replayOnEnterRef, revealRef } from "../siteKit/motion/reveal";
import { SiteText, SiteTitle } from "../siteKit/SiteText";
import { SiteSection } from "../siteKit/SiteSection";
import { riseOrder, SITE_HERO_RISE_STYLES } from "../siteKit/siteHeroStyles";

const STANDARD_COLOR = "#448CFF";
const ADVANCED_COLOR = "#123C82";

const PLAN_STYLES = {
  stage: {
    margin: "clamp(8px,1.2vw,16px) clamp(12px,1.6vw,24px) 0",
    borderRadius: SITE_VAR.radiusImage,
    border: "1px solid #BAD3FF",
    background:
      "radial-gradient(ellipse 72% 82% at 50% 36%,#FFFFFF 0%,#EFF5FF 46%,#C9DBFF 100%)",
    padding: "clamp(72px,9.6vw,144px) var(--gutter) clamp(220px,24vw,340px)",
    textAlign: "center",
    [SITE_MEDIA.phone]: { margin: "8px 8px 0", paddingBottom: "160px" },
  },
  title: SITE_TYPE.h1,
  lead: {
    ...SITE_TYPE.lead,
    margin: "28px auto 0",
    color: SITE_COLOR.ink2,
    maxWidth: "34em",
  },
  plans: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: "clamp(16px,2.4vw,32px)",
    // 카드 폭은 여백과 따로 정한다 — 여백이 폭에 따라 늘어도(marketing-site-shell) 넓은 화면의 카드 폭은 그대로
    maxWidth: `calc(952px + ${SITE_VAR.gutter} * 2)`,
    margin: "calc(-1 * clamp(160px,18vw,250px)) auto 0",
    paddingInline: SITE_VAR.gutter,
    [SITE_MEDIA.tablet]: {
      gridTemplateColumns: "1fr",
      maxWidth: `calc(520px + ${SITE_VAR.gutter} * 2)`,
    },
    [SITE_MEDIA.phone]: { marginTop: "-120px", paddingInline: "16px" },
    // 가로 터치 화면: 두 플랜을 나란히 두어 한 화면에서 비교한다
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "repeat(2,minmax(0,1fr))",
      maxWidth: `calc(952px + ${SITE_VAR.gutter} * 2)`,
    },
  },
  card: {
    position: "relative",
    background: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 0 0 1px rgba(15,23,42,.05),0 12px 36px rgba(28,44,66,.12)",
    transition: `transform .5s ${SITE_EASE.out},box-shadow .5s ${SITE_EASE.out}`,
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 0 0 1px rgba(15,23,42,.05),0 22px 48px rgba(28,44,66,.16)",
    },
    "&::before": {
      content: '""',
      display: "block",
      height: "7px",
      background: "var(--c)",
    },
  },
  cardInner: {
    padding:
      "clamp(24px,2.8vw,36px) clamp(22px,2.6vw,32px) clamp(22px,2.4vw,28px)",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  name: {
    fontSize: "clamp(26px,2.4vw,32px)",
    fontWeight: 700,
    letterSpacing: "-.03em",
    lineHeight: 1.25,
    margin: "0 0 12px",
  },
  description: {
    fontSize: "clamp(15.5px,1.2vw,17px)",
    lineHeight: 1.6,
    color: SITE_COLOR.ink,
    "& b": { color: "var(--c)", fontWeight: 600 },
  },
  features: {
    listStyle: "none",
    margin: "22px 0 28px",
    padding: "22px 0 0",
    borderTop: `1px solid ${SITE_COLOR.line}`,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: 1,
  },
  feature: {
    display: "flex",
    gap: "10px",
    fontSize: "16px",
    lineHeight: 1.55,
    color: SITE_COLOR.ink2,
    whiteSpace: "pre-line",
  },
  check: {
    flex: "none",
    width: "18px",
    height: "18px",
    marginTop: "4px",
    borderRadius: "50%",
    background: "var(--c)",
    display: "grid",
    placeItems: "center",
    "& svg": { width: "11px", height: "11px", color: "#fff" },
  },
  go: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "56px",
    padding: "0 22px",
    borderRadius: "12px",
    background: "var(--c)",
    color: "#fff",
    fontSize: "16px",
    fontWeight: 700,
    letterSpacing: ".2px",
    transition: "filter .2s,transform .2s",
    "&:hover": { filter: "brightness(1.08)" },
    "&:active": { transform: "scale(.99)" },
    "& svg": { color: "#fff", opacity: 0.9 },
  },
  compare: {
    ...SITE_WRAP,
    marginTop: SITE_VAR.section,
    [SITE_MEDIA.phone]: { marginTop: "96px" },
  },
  compareTitle: { ...SITE_TYPE.h3, marginBottom: "32px" },
  tableWrap: {
    overflowX: "auto",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: `0 0 0 1px ${SITE_COLOR.line}`,
    padding: "4px 16px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "16px",
    "& th, & td": {
      textAlign: "left",
      padding: "20px 16px",
      borderBottom: `1px solid ${SITE_COLOR.line}`,
      verticalAlign: "top",
    },
    "& tbody tr:last-child th, & tbody tr:last-child td": { borderBottom: 0 },
    "& thead th": {
      fontSize: "15px",
      fontWeight: 700,
      borderBottom: `2px solid ${SITE_COLOR.line}`,
    },
    "& thead th[data-plan='standard']": { color: SITE_COLOR.blue },
    "& thead th[data-plan='advanced']": { color: ADVANCED_COLOR },
    "& tbody th": { fontWeight: 600, color: SITE_COLOR.ink2, width: "24%" },
    "& td": { width: "38%", color: SITE_COLOR.ink },
    [SITE_MEDIA.tablet]: { minWidth: "560px" },
  },
  relatedTitle: SITE_TYPE.h3,
  relatedGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: "clamp(24px,3vw,40px)",
    marginTop: "clamp(32px,4vw,48px)",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr" },
    // 가로 터치 화면: 한 줄에 하나면 카드 그림 하나가 화면 두 개 높이다 — 넓은 화면처럼 두 칸
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    },
  },
} as const;

const PLAN_KEYS = ["standard", "advanced"] as const;
/** 그리기 전 자리 높이(넓은 화면, 900px 이하) — 시안 `--cis`·`--cism` */
const RELATED_OFFSCREEN_SIZE = [900, 1300] as const;
const PLAN_COLORS = {
  standard: STANDARD_COLOR,
  advanced: ADVANCED_COLOR,
} as const;

const CHECK_PATH = "M2.2 6.3l2.5 2.5 5.1-5.6";
const ARROW_PATH = "M7 4l5 5-5 5";

interface CompareRow {
  label: string;
  standard: string;
  advanced: string;
}

/** 이용 플랜(`/plan`, marketing-service-pages) — 시안 plan.html 그대로. */
export function PlanPage() {
  const { t } = useTranslation("landing");
  const titleId = useId();
  const compareTitleId = useId();
  const relatedTitleId = useId();
  const rows = t("site.plan.compare.rows", {
    returnObjects: true,
  }) as readonly CompareRow[];
  return (
    <>
      <Box component="section" sx={PLAN_STYLES.stage} aria-labelledby={titleId}>
        <Box ref={replayOnEnterRef} sx={SITE_HERO_RISE_STYLES}>
          <Box component="h1" id={titleId} sx={PLAN_STYLES.title}>
            <SiteTitle text={t("site.plan.title")} />
          </Box>
          <Box component="p" style={riseOrder(1)} sx={PLAN_STYLES.lead}>
            <SiteText text={t("site.plan.lead")} />
          </Box>
        </Box>
      </Box>
      <section aria-label={t("site.plan.plansLabel")}>
        <Box sx={PLAN_STYLES.plans}>
          {PLAN_KEYS.map((plan, index) => {
            const features = t(`site.plan.${plan}.features`, {
              returnObjects: true,
            }) as readonly string[];
            return (
              <Box
                component="article"
                key={plan}
                ref={revealRef}
                data-reveal="up"
                style={
                  { "--i": index, "--c": PLAN_COLORS[plan] } as CSSProperties
                }
                sx={PLAN_STYLES.card}
              >
                <Box sx={PLAN_STYLES.cardInner}>
                  <Box component="h2" sx={PLAN_STYLES.name}>
                    <SiteTitle text={t(`site.plan.${plan}.name`)} />
                  </Box>
                  <Box component="p" sx={PLAN_STYLES.description}>
                    <SiteText text={t(`site.plan.${plan}.description`)} />
                  </Box>
                  <Box component="ul" sx={PLAN_STYLES.features}>
                    {features.map((feature) => (
                      <Box
                        component="li"
                        key={feature}
                        sx={PLAN_STYLES.feature}
                      >
                        <Box component="i" sx={PLAN_STYLES.check}>
                          <svg viewBox="0 0 12 12" aria-hidden="true">
                            <path
                              d={CHECK_PATH}
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.9"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Box>
                        {feature}
                      </Box>
                    ))}
                  </Box>
                  <Box component={Link} href="/inquiry" sx={PLAN_STYLES.go}>
                    {t("site.plan.ask")}
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      aria-hidden="true"
                    >
                      <path
                        d={ARROW_PATH}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </section>

      <Box
        component="section"
        sx={PLAN_STYLES.compare}
        aria-labelledby={compareTitleId}
      >
        <Box
          component="h2"
          id={compareTitleId}
          ref={revealRef}
          data-reveal="up"
          sx={PLAN_STYLES.compareTitle}
        >
          <SiteTitle text={t("site.plan.compare.title")} />
        </Box>
        <Box ref={revealRef} data-reveal="up" sx={PLAN_STYLES.tableWrap}>
          <Box component="table" sx={PLAN_STYLES.table}>
            <thead>
              <tr>
                <th scope="col">{t("site.plan.compare.item")}</th>
                {PLAN_KEYS.map((plan) => (
                  <th key={plan} scope="col" data-plan={plan}>
                    {t(`site.plan.${plan}.name`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <th scope="row">{row.label}</th>
                  <td>{row.standard}</td>
                  <td>{row.advanced}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </Box>

      <SiteSection
        labelledBy={relatedTitleId}
        skipOffscreen={RELATED_OFFSCREEN_SIZE}
      >
        <Box sx={SITE_WRAP}>
          <Box
            component="h2"
            id={relatedTitleId}
            ref={revealRef}
            data-reveal="up"
            sx={PLAN_STYLES.relatedTitle}
          >
            <SiteTitle text={t("site.plan.related.title")} />
          </Box>
          <Box sx={PLAN_STYLES.relatedGrid}>
            <LinkCard
              href="/accurate"
              revealIndex={0}
              media={
                <SiteFileImage
                  file="art/tile_platform.svg"
                  width={600}
                  height={400}
                  alt={t("site.plan.related.platform.alt")}
                />
              }
              kicker={t("site.plan.related.platform.kicker")}
              title={t("site.plan.related.platform.title")}
              text={t("site.plan.related.platform.text")}
            />
            <LinkCard
              href="/survey"
              revealIndex={1}
              media={
                <SiteImage
                  name="drone_work"
                  sizes="(max-width: 900px) 100vw, 600px"
                  alt={t("site.plan.related.survey.alt")}
                />
              }
              kicker={t("site.plan.related.survey.kicker")}
              title={t("site.plan.related.survey.title")}
              text={t("site.plan.related.survey.text")}
            />
          </Box>
        </Box>
      </SiteSection>

      <ClosingContact
        eyebrow={t("site.plan.closing.eyebrow")}
        title={<SiteTitle text={t("site.plan.closing.title")} />}
      />
    </>
  );
}
